<template>
    <div class="modal-overlay">
        <div class="modal-box">
            <!-- Game Over / Start Screen -->
            <div class="button" v-if="gameState == 'off'">
                <div class="modalText">Connect the two daily words by clicking on words in their definitions! Only 4+ letter words are clickable!</div>
                <base-button @click="startGame()">Start Game!</base-button>
                <base-button @click="setWin()">win game</base-button>
            </div>

            <!-- Win Screen -->
            <div class="button" v-if="gameState == 'win'">
                <div class="modalText">You Win! <br>Your winning word was "{{ winningWord }}" Total
                    links: {{ totalLinks }}
                    <br>
                </div>

                <!-- 1. Initials Input Box: Hides after submission -->
                <div class="arcade-input-container" v-if="!scoreSubmitted">
                    <input type="text" id="arcadeInput" :value="initials" @input="handleInput" maxlength="3"
                        autocomplete="off" autocorrect="off" spellcheck="false" />

                    <div class="input-boxes" aria-hidden="true">
                        <div class="box" :class="{ 'is-active': initials.length > 0 }">{{ initials[0] || '_' }}</div>
                        <div class="box" :class="{ 'is-active': initials.length > 1 }">{{ initials[1] || '_' }}</div>
                        <div class="box" :class="{ 'is-active': initials.length > 2 }">{{ initials[2] || '_' }}</div>
                    </div>
                </div>

                <!-- 2. Leaderboard Section: Revealed only after submission -->
                <div class="leaderboard-container" v-if="this.showLeaderboard">
                    <h3 class="leaderboard-title">Top Scores</h3>

                    <!-- Shows while the Firestore async query executes -->
                    <div v-if="isLoading" class="leaderboard-loading">Loading...</div>

                    <!-- Loops through and displays scores if any documents exist -->
                    <div v-else-if="leaderboard && leaderboard.length > 0" class="leaderboard-list">
                        <div v-for="(score, index) in leaderboard" :key="score.id" class="leaderboard-item">
                            <span>{{ index + 1 }}.{{ score.initials }}</span>
                            <span> Links:{{ score.totalLinks }} Freq.Score:{{ score.freq }}</span>
                        </div>
                    </div>

                    <!-- Fallback message for empty databases -->
                    <div v-else class="leaderboard-empty">No scores yet!</div>
                </div>

                <!-- 3. Submit Button: Hides after submission -->
                <base-button @click="submitScore" v-if="!scoreSubmitted">Submit Score</base-button>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebaseInit.js';

export default {
    props: ['gameState', 'winningWord', 'totalLinks', 'avgFreqScore'],
    emits: ['start-game', 'win-game', 'submit-score'],
    data() {
        return {
            initials: '',
            leaderboard: [],
            isLoading: false,
            showLeaderboard: false,
            scoreSubmitted: false
        };
    },
    mounted() {
        // We removed fetchLeaderboard() from here to save database reads until the score is actually submitted!
    },
    methods: {
        startGame() {
            console.log('modalbutton click!');
            this.$emit('start-game');
        },
        setWin() {
            console.log('winbutton click!');
            this.$emit('win-game');
        },
        handleInput(event) {
            // Force uppercase and limit to 3 characters
            this.initials = event.target.value.toUpperCase().slice(0, 3);
        },
        async submitScore() {
            if (!this.initials || this.initials.trim().length === 0) {
                alert("Please enter your initials first!");
                return;
            }
            if (this.totalLinks == 0) {
                alert("I don't think you played, no score submission!");
                this.showLeaderboard = true
                this.fetchLeaderboard();
                return
            }

            // 1. Pause execution here until parent executes resolve()
            const responseData = await new Promise((resolve) => {
                this.$emit('submit-score', {
                    initials: this.initials,
                    totalLinks: this.totalLinks,
                    avgFreqScore: this.avgFreqScore,
                    resolve // Passing resolve enables the child to receive responseData
                });
            });

            // 2. Execution resumes here automatically with the return value!
            console.log("Response data in child:", responseData);

            if (responseData && responseData.success && responseData.updated) {
                this.isNewHighScore = true; // Use this to conditionally update UI
            } else {
                this.isNewHighScore = false;
            }

            this.scoreSubmitted = true;
            this.fetchLeaderboard();
        },
        async fetchLeaderboard() {
            console.log('fetching??')
            this.isLoading = true;
            try {
                const leaderboardCollection = collection(db, "highScores");

                // 1. Fetch the raw top 15 by totalLinks
                const leaderboardQuery = query(
                    leaderboardCollection,
                    orderBy("totalLinks", "asc"),
                    limit(15)
                );

                const querySnapshot = await getDocs(leaderboardQuery);
                const topScores = [];
                querySnapshot.forEach((doc) => {
                    topScores.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });

                // 2. Filter out zeros, sort with our tiebreaker, and take the top 10
                this.leaderboard = topScores
                    .filter(score => score.freq !== 0 && score.freq !== undefined)
                    .sort((a, b) => {
                        // If totalLinks are different, sort by totalLinks ascending
                        if (a.totalLinks !== b.totalLinks) {
                            return a.totalLinks - b.totalLinks;
                        }
                        // If totalLinks are a tie, sort by freq ascending (lower is better)
                        return a.freq - b.freq;
                    })
                    .slice(0, 10);

                console.log("Leaderboard updated with tiebreaker:", this.leaderboard);
                this.showLeaderboard = true

            } catch (error) {
                console.error("Error fetching leaderboard:", error);
            } finally {
                this.isLoading = false;
                this.showLeaderboard = true
            }
        }
    }
};
</script>


<style scoped>
.arcade-input-container {
    position: relative;
    display: inline-block;
    margin: 1rem 0;
}

/* Make the real input completely invisible, but clickable across the whole box area */
#arcadeInput {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    font-size: 24px;
    /* Matches box scale for mobile keyboards */
    z-index: 2;
}

/* Style your visual boxes */
.input-boxes {
    display: flex;
    gap: 12px;
}

.leaderboard-list {
    font-size: 22px;
}

/* Base dim/off state */
.box {
    width: 50px;
    height: 60px;
    border: 3px solid black;
    background-color: #111;
    color: #444;
    /* Dim placeholder text/underscore when inactive */
    font-family: 'Courier New', monospace;
    font-size: 32px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    transition: all 0.2s ease;
}

/* Illuminated state triggered per box as characters are typed */
.box.is-active {
    border-color: wheat;
    color: wheat;
    background-color: #1a1813;
    /* Subtle warm glow background */
    box-shadow: 0 0 14px gold;
    /* Radiant gold illumination */
}

.button {
    font-family: "Courier Prime", "Courier New", Courier, monospace;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    /* Adjust space between text, inputs, and button */

}

/* 1. THE SHADOW OVERLAY: Spans the whole screen, darkens everything behind it */
.modalText {
    margin-bottom: .75rem;
    font-family: "Courier Prime", "Courier New", Courier, monospace;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(50, 39, 69, 0.55);
    /* Your custom purple shadow tint */
    z-index: 1000;
    /* Sits on top of the main website */

    /* Flexbox magic: This automatically centers the modal-box perfectly */
    display: flex;
    justify-content: center;
    align-items: center;
}

/* 2. THE MODAL BOX: The clean, crisp white card in the center */
.modal-box {
    background-color: #b5a3d5;
    color: #ffffff;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
    /* Slightly deeper shadow looks great on dark overlays */
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 1.5rem;
    text-align: center;
    /* Centers your game rules beautifully */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    max-width: 500px;
    width: 90%;
}
</style>